import React, { memo, useEffect, useState } from 'react';
import { FiFile, FiFilm, FiImage, FiX } from 'react-icons/fi';

import { fileKind, formatFileSize } from './quickInferenceUtils';

interface Props {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

const KIND_STYLES = {
  dicom: {
    icon: FiFile,
    label: 'DICOM',
    pill: 'bg-blue-100 text-blue-700',
    iconBg: 'bg-blue-50 text-blue-500',
  },
  video: {
    icon: FiFilm,
    label: 'Video',
    pill: 'bg-violet-100 text-violet-700',
    iconBg: 'bg-violet-50 text-violet-500',
  },
  image: {
    icon: FiImage,
    label: 'Image',
    pill: 'bg-emerald-100 text-emerald-700',
    iconBg: 'bg-emerald-50 text-emerald-500',
  },
} as const;

export const QuickInferenceFilePreviewCard = memo(function QuickInferenceFilePreviewCard({
  file,
  onRemove,
  disabled,
}: Props) {
  const kind = fileKind(file);
  const style = KIND_STYLES[kind];
  const Icon = style.icon;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;

    if (kind === 'image' || file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      revoked = url;
      setPreviewUrl(url);
      return () => {
        cancelled = true;
        if (revoked) URL.revokeObjectURL(revoked);
      };
    }

    if (kind === 'video' || file.type.startsWith('video/')) {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.src = objectUrl;

      const cleanupVideo = () => {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      const capture = () => {
        if (cancelled) {
          cleanupVideo();
          return;
        }
        try {
          const canvas = document.createElement('canvas');
          const w = video.videoWidth || 320;
          const h = video.videoHeight || 240;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx || !w || !h) {
            setPreviewUrl(null);
            cleanupVideo();
            return;
          }
          ctx.drawImage(video, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              cleanupVideo();
              if (cancelled || !blob) {
                setPreviewUrl(null);
                return;
              }
              const thumbUrl = URL.createObjectURL(blob);
              revoked = thumbUrl;
              setPreviewUrl(thumbUrl);
            },
            'image/jpeg',
            0.82
          );
        } catch {
          cleanupVideo();
          if (!cancelled) setPreviewUrl(null);
        }
      };

      video.onloadeddata = () => {
        // Seek slightly into the clip for a non-black first frame when possible.
        try {
          if (video.duration && Number.isFinite(video.duration) && video.duration > 0.1) {
            video.currentTime = Math.min(0.1, video.duration / 2);
          } else {
            capture();
          }
        } catch {
          capture();
        }
      };
      video.onseeked = capture;
      video.onerror = () => {
        cleanupVideo();
        if (!cancelled) setPreviewUrl(null);
      };

      return () => {
        cancelled = true;
        cleanupVideo();
        if (revoked) URL.revokeObjectURL(revoked);
      };
    }

    setPreviewUrl(null);
    return () => {
      cancelled = true;
    };
  }, [file, kind]);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-100 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md hover:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:ring-gray-700">
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900/70 text-white opacity-0 backdrop-blur-sm transition group-hover:opacity-100 disabled:opacity-40"
        aria-label="Remove file"
      >
        <FiX size={11} />
      </button>

      <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        {previewUrl ? (
          <img src={previewUrl} alt={file.name} className="h-full w-full object-contain p-1" />
        ) : (
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${style.iconBg}`}>
            <Icon size={20} />
          </div>
        )}
        <span className="absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[9px] font-medium text-white backdrop-blur-sm">
          {formatFileSize(file.size)}
        </span>
      </div>

      <div className="border-t border-slate-50 px-2 py-2 dark:border-gray-700">
        <p className="truncate text-xs font-medium text-slate-800 dark:text-gray-100" title={file.name}>
          {file.name}
        </p>
        <span
          className={`mt-1 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${style.pill}`}
        >
          {style.label}
        </span>
      </div>
    </div>
  );
});
